import React, { forwardRef } from 'react';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { CAYMAN_ACADEMY_LOGO_BASE64 } from '../../assets/images/caymanAcademyLogoBase64';
import { CxcRegistrationReceipt, CxcSubject } from '../../types';
import {
  CxcFeesView,
  CxcFormState,
  CxcSittingView,
  CxcSubjectSelection,
  formatMoney,
  normalizeDateInput,
} from '../../utils/cxcRegistration';

// Web-only (react-native-web). The public website renders this document as plain
// DOM + a CSS module (publicWebsite/CaymanAcademy/.../CxcPrintDocument.tsx); to
// look the same here, it is the same DOM and the same stylesheet, not a
// StyleSheet.create() approximation. A plain string handed to JSX renders as
// that DOM tag - the trick CxcRegistrationScreen.web.tsx already uses for
// <input> and <canvas>.
const Div: any = 'div';
const Span: any = 'span';
const Img: any = 'img';
const P: any = 'p';
const A: any = 'a';
const H1: any = 'h1';
const H2: any = 'h2';
const H3: any = 'h3';
const H4: any = 'h4';
const U: any = 'u';
const B: any = 'b';
const Strong: any = 'strong';
const Table: any = 'table';
const Thead: any = 'thead';
const Tbody: any = 'tbody';
const Tr: any = 'tr';
const Th: any = 'th';
const Td: any = 'td';
const Button: any = 'button';
const StyleTag: any = 'style';

/**
 * CxcPrintDocument.module.css and the preview-modal rules of
 * CxcRegistrationForm.module.css from the public website, verbatim apart from a
 * `cxcpd-` prefix (this app has no CSS modules to scope them). Rendered as a
 * <style> tag next to the document, so it is in document.styleSheets and
 * printRegistrationForm copies it into the print iframe - which is also where
 * the @media print block (letterhead, divider, footer) takes effect.
 */
export const CXC_PRINT_CSS = `
.cxcpd-officialDocPaper { border-radius: 8px; margin: 0 auto; max-width: 880px; color: #111827; background: #ffffff; padding: 28px 32px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); font-family: Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; text-align: left; }
.cxcpd-officialDocPaper * { box-sizing: border-box; }

.cxcpd-printHeader { display: none; grid-template-columns: 1.3fr 1.25fr 1.15fr; align-items: center; gap: 12px; margin-bottom: 8px; }
.cxcpd-printHeaderLeft { display: flex; align-items: center; gap: 12px; }
.cxcpd-printLogo { width: 46px; height: 58px; object-fit: contain; flex-shrink: 0; }
.cxcpd-printSchoolTitle { display: flex; flex-direction: column; }
.cxcpd-printSchoolName { margin: 0; font-size: 19px; font-weight: 800; color: #004840; line-height: 1.05; text-transform: uppercase; letter-spacing: 0.3px; display: flex; flex-direction: column; white-space: nowrap; }
.cxcpd-printSchoolMotto { margin: 3px 0 0 0; font-size: 7px; font-weight: 700; color: #004840; text-transform: uppercase; letter-spacing: 0.3px; }
.cxcpd-printHeaderCenter { font-size: 9.5px; line-height: 1.5; color: #004840; font-weight: 600; }
.cxcpd-printHeaderCenter p { margin: 0 0 1px 0; white-space: nowrap; }
.cxcpd-printHeaderCenter a { color: #004840; text-decoration: none; font-weight: 600; }
.cxcpd-printHeaderRight { font-size: 9px; line-height: 1.5; color: #004840; font-weight: 600; text-align: left; }
.cxcpd-printHeaderRight h4 { margin: 0 0 3px 0; font-size: 9.5px; font-weight: 800; color: #004840; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; }
.cxcpd-printHeaderRight p { margin: 0 0 2px 0; white-space: nowrap; }
.cxcpd-printDivider { display: none; height: 3px; background-color: #004840; border: none; margin: 10px 0 18px 0; }

.cxcpd-printTitleBlock { text-align: center; margin-bottom: 20px; }
.cxcpd-printFormTitle { margin: 0; font-size: 20px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #000000; }
.cxcpd-printAcademicYear { display: inline-block; margin-top: 4px; font-size: 14px; font-weight: 700; color: #000000; background: #fef08a; padding: 2px 14px; border-radius: 3px; }
.cxcpd-printMetaRow { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 13.5px; font-weight: 600; color: #000000; }
.cxcpd-printMetaCol { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cxcpd-printUnderlineValue { display: inline-block; min-width: 130px; border-bottom: 1.5px solid #000000; padding: 0 6px 1px; font-weight: 600; color: #000000; text-transform: uppercase; }
.cxcpd-printCheckboxItem { display: inline-flex; align-items: center; gap: 6px; margin-left: 10px; }
.cxcpd-printBox { display: inline-flex; align-items: center; justify-content: center; width: 17px; height: 17px; border: 1.5px solid #000000; font-size: 13px; font-weight: 800; color: #000000; line-height: 1; }
.cxcpd-printNoticeBanner { font-weight: 500; font-size: 12.5px; font-style: italic; color: #475569; margin: 18px 0 14px 0; padding: 8px 12px; background: #f8fafc; border-left: 3px solid #004840; border-radius: 4px; }

.cxcpd-printFormBody { display: flex; gap: 20px; align-items: flex-start; margin-bottom: 20px; }
.cxcpd-printFieldsColumn { flex: 1; display: flex; flex-direction: column; gap: 12px; }
.cxcpd-printFieldsRow { display: flex; justify-content: space-between; gap: 20px; }
.cxcpd-printFieldItem { flex: 1; display: flex; align-items: baseline; gap: 6px; font-size: 13px; }
.cxcpd-printFieldItemFull { width: 100%; }
.cxcpd-printFieldLabel { font-weight: 700; color: #000000; white-space: nowrap; }
.cxcpd-printFieldValue { flex: 1; border-bottom: 1px solid #000000; padding: 0 4px 1px; font-weight: 600; color: #0f172a; min-height: 18px; text-transform: uppercase; }
.cxcpd-printPhotoContainer { width: 125px; height: 145px; border: 1.5px solid #475569; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f8fafc; flex-shrink: 0; }
.cxcpd-printPhotoImg { width: 100%; height: 100%; object-fit: cover; }
.cxcpd-printPhotoPlaceholder { text-align: center; color: #64748b; font-size: 11px; padding: 10px; }

.cxcpd-printSectionTitle { font-size: 14px; font-weight: 600; text-transform: uppercase; color: #000000; margin: 22px 0 8px 0; border-bottom: 1.5px solid #004840; padding-bottom: 4px; }
.cxcpd-printTable { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
.cxcpd-printTable th { border: 1px solid #334155; background-color: #f1f5f9; padding: 6px 10px; font-size: 11.5px; font-weight: 700; color: #000000; text-align: left; }
.cxcpd-printTable td { border: 1px solid #334155; padding: 6px 10px; font-size: 11.5px; color: #000000; }
.cxcpd-printColSubject { width: 42%; }
.cxcpd-printColProficiency { width: 18%; }
.cxcpd-printColEntry { width: 26%; }
.cxcpd-printColCost { width: 14%; text-align: right; }
.cxcpd-printColDesc { width: 70%; }
.cxcpd-printColAmount { width: 30%; text-align: right; }
.cxcpd-printRight { text-align: right; }
.cxcpd-printRightBold { text-align: right; font-weight: 600; }
.cxcpd-printResit { font-weight: 600; color: rgb(27, 112, 209); }
.cxcpd-printTotalRow { background: #f8fafc; font-weight: 700; }
.cxcpd-printTotalAmount { text-align: right; color: #134e4a; font-size: 11pt; }
.cxcpd-printInvoiceRef { margin: 8px 0 16px; padding: 8px 12px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 4px; font-size: 12px; font-weight: 600; color: #166534; }

.cxcpd-printCommitment { font-size: 11px; line-height: 1.5; color: #1e293b; margin: 6px 0 10px; background: #f8fafc; padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 4px; }
.cxcpd-printCommitment u, .cxcpd-printCommitment b { text-transform: uppercase; }
.cxcpd-printSignaturesGrid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 18px; page-break-inside: avoid; break-inside: avoid; }
.cxcpd-printSigBox { border: 1px solid #94a3b8; border-radius: 6px; padding: 12px; background: #fafafa; display: flex; flex-direction: column; justify-content: space-between; }
.cxcpd-printSigLabel { font-size: 12px; font-weight: 700; color: #000000; margin-bottom: 6px; }
.cxcpd-printSigImage { max-width: 100%; height: 60px; object-fit: contain; margin-bottom: 6px; }
.cxcpd-printSigBlank { height: 55px; border-bottom: 1px solid #000; }
.cxcpd-printSigDate { font-size: 11px; color: #475569; border-top: 1px solid #cbd5e1; padding-top: 4px; margin-top: 4px; }
.cxcpd-printFooterNote { display: none; text-align: center; margin-top: 24px; padding-top: 12px; border-top: 2px solid #004840; font-size: 10.5px; font-weight: 700; letter-spacing: 0.2px; color: #004840; }

@keyframes cxcpd-fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes cxcpd-modalScaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
.cxcpd-modalOverlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 99999; padding: 16px; animation: cxcpd-fadeIn 0.2s ease; }
.cxcpd-previewModalContainer { background: #ffffff; width: 100%; max-width: 980px; height: 92vh; max-height: 92vh; border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35); display: flex; flex-direction: column; position: relative; overflow: hidden; animation: cxcpd-modalScaleIn 0.25s ease; }
.cxcpd-modalHeader { display: flex; align-items: center; justify-content: space-between; padding: 14px 22px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; flex-shrink: 0; }
.cxcpd-modalHeaderGroup { display: flex; align-items: center; gap: 8px; }
.cxcpd-modalHeaderTitle { font-size: 17px; font-weight: 700; color: #004840; margin: 0; }
.cxcpd-modalHeaderSubtitle { margin-top: 3px; font-size: 12.5px; color: #475569; display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.cxcpd-statusPill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: 6px; }
.cxcpd-statusPill-pending { background: #fef3c7; color: #b45309; }
.cxcpd-statusPill-paid { background: #dcfce7; color: #15803d; }
.cxcpd-statusPill-done { background: #dbeafe; color: #1d4ed8; }
.cxcpd-modalCloseBtn { background: transparent; border: none; color: #64748b; cursor: pointer; padding: 6px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease; }
.cxcpd-modalCloseBtn:hover { background: #e2e8f0; color: #0f172a; }
.cxcpd-modalScrollBody { flex: 1; overflow-y: auto; padding: 24px; background: #f1f5f9; }
.cxcpd-modalFooter { display: flex; justify-content: space-between; align-items: center; padding: 14px 24px; border-top: 1px solid #e2e8f0; background: #ffffff; gap: 12px; flex-wrap: wrap; flex-shrink: 0; }
.cxcpd-modalFooterActions { display: flex; align-items: center; gap: 10px; }
.cxcpd-alertBox { display: flex; align-items: center; gap: 10px; background: #fef2f2; border: 1px solid #fecaca; border-left: 4px solid #ef4444; border-radius: 6px; padding: 12px 16px; margin-bottom: 16px; color: #991b1b; font-size: 14px; }
.cxcpd-printBtn { background: #16a34a; color: #ffffff; border: 1px solid #15803d; padding: 10px 18px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s ease; font-family: inherit; }
.cxcpd-printBtn:hover { background: #15803d; }
.cxcpd-cancelBtn { background: #ffffff; border: 1px solid rgb(27, 112, 209); color: rgb(27, 112, 209); padding: 10px 18px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s ease; font-family: inherit; }
.cxcpd-cancelBtn:hover { background: #eef2ff; color: #084298; border-color: #084298; }
.cxcpd-cancelBtn:disabled { opacity: 0.6; cursor: not-allowed; }
.cxcpd-primarybtn { padding: 11px 32px; border: none; border-radius: 6px; cursor: pointer; background: rgb(27, 112, 209); color: #ffffff; font-size: 15px; font-weight: 500; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; font-family: inherit; }
.cxcpd-primarybtn:hover { background: #084298; }
.cxcpd-primarybtn:disabled { opacity: 0.6; cursor: not-allowed; }

@media print {
  .cxcpd-officialDocPaper { background: #ffffff !important; border: none !important; box-shadow: none !important; padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
  .cxcpd-printHeader { display: grid !important; }
  .cxcpd-printFooterNote { display: block !important; }
  .cxcpd-printAcademicYear { background: #fef08a !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .cxcpd-printDivider { display: block !important; background-color: #004840 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .cxcpd-printTable th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

const currency = (n: number) => formatMoney(n);

/** "12 / 04 / 2010" from YYYY-MM-DD, blanks when nothing was entered - as the paper form prints it. */
const formatDobForPrint = (typed: string) => {
  const iso = normalizeDateInput(typed) ?? typed;
  if (!iso) return '    /    /    ';
  const parts = iso.split('-');
  return parts.length === 3 ? `${parts[2]} / ${parts[1]} / ${parts[0]}` : iso;
};

const todayForPrint = () =>
  new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

export interface CxcPrintDocumentProps {
  form: CxcFormState;
  /** cxcCentre(options) - the centre the server reports for the CA form. */
  centre: { name: string; number: string };
  /** cxcSittings(options): every sitting on offer, ticked where it is the chosen one. */
  sittings: CxcSittingView[];
  fees: CxcFeesView;
  selectedSubjects: CxcSubject[];
  selection: CxcSubjectSelection;
  photoDataUrl: string | null;
  candidateSignatureData: string | null;
  parentSignatureData: string | null;
  estimatedTotal: number;
  receipt: CxcRegistrationReceipt | null;
}

/**
 * The official CSEC Registration Form as a document: shown in the preview
 * modal (letterhead hidden) and printed as-is (letterhead, divider and footer
 * note appear via @media print in the stylesheet above). Same markup as the
 * public website's CxcPrintDocument; only the data is this form's.
 */
const CxcPrintDocument = forwardRef<any, CxcPrintDocumentProps>(function CxcPrintDocument(
  { form, centre, sittings, fees, selectedSubjects, selection, photoDataUrl, candidateSignatureData, parentSignatureData, estimatedTotal, receipt },
  ref,
) {
  const candidateFullName = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ');
  const chosen = sittings.find(s => s.id === form.sitting);
  const examYear = chosen?.year ?? sittings[0]?.year ?? new Date().getFullYear() + 1;

  return (
    <Div ref={ref} id="cxcPrintDocument" className="cxcpd-officialDocPaper">
      <StyleTag>{CXC_PRINT_CSS}</StyleTag>

      {/* 1. Official letterhead (print-only) */}
      <Div className="cxcpd-printHeader">
        <Div className="cxcpd-printHeaderLeft">
          <Img src={CAYMAN_ACADEMY_LOGO_BASE64} alt="Cayman Academy Crest" className="cxcpd-printLogo" />
          <Div className="cxcpd-printSchoolTitle">
            <H1 className="cxcpd-printSchoolName">
              <Span>CAYMAN</Span>
              <Span>ACADEMY</Span>
            </H1>
            <P className="cxcpd-printSchoolMotto">A SEVENTH-DAY ADVENTIST INSTITUTION</P>
          </Div>
        </Div>

        <Div className="cxcpd-printHeaderCenter">
          <P>211 Walkers Road, George Town,</P>
          <P>Cayman Island. KY1-1106</P>
          <P><A href="mailto:Office.secretary@caymanacademy.ky">Office.secretary@caymanacademy.ky</A></P>
          <P><A href="https://www.caymanacademy.ky" target="_blank" rel="noreferrer">www.caymanacademy.ky</A></P>
          <P>1 (345) 640-2630</P>
        </Div>

        <Div className="cxcpd-printHeaderRight">
          <H4>CONTACT HOURS</H4>
          <P>Monday - Thursday : 8:30 AM - 5:00 PM</P>
          <P>Friday: 8:30 AM - 1:00 PM</P>
        </Div>
      </Div>

      <Div className="cxcpd-printDivider" />

      {/* 2. Title & sitting period */}
      <Div className="cxcpd-printTitleBlock">
        <H2 className="cxcpd-printFormTitle">CSEC REGISTRATION FORM</H2>
        <Div className="cxcpd-printAcademicYear">{centre.name} {examYear - 1} - {examYear}</Div>
      </Div>

      {/* 3. Centre details & sitting */}
      <Div className="cxcpd-printMetaRow">
        <Div className="cxcpd-printMetaCol">
          <Span className="cxcpd-printFieldLabel">Centre Name:</Span>
          <Span>{centre.name}</Span>
        </Div>
        <Div className="cxcpd-printMetaCol">
          <Span className="cxcpd-printFieldLabel">Centre No.:</Span>
          <Span>{centre.number}</Span>
        </Div>
      </Div>

      <Div className="cxcpd-printMetaRow">
        <Div className="cxcpd-printMetaCol">
          <Span className="cxcpd-printFieldLabel">Grade/Class:</Span>
          <Span className="cxcpd-printUnderlineValue">{form.gradeOrClass || '____________________'}</Span>
        </Div>
        <Div className="cxcpd-printMetaCol">
          <Span className="cxcpd-printFieldLabel">Month/Year:</Span>
          {sittings.map(s => (
            <Span key={s.id} className="cxcpd-printCheckboxItem">
              {s.label} <Span className="cxcpd-printBox">{s.id === form.sitting ? '✓' : ''}</Span>
            </Span>
          ))}
        </Div>
      </Div>

      {/* 4. Instructions */}
      <Div className="cxcpd-printNoticeBanner">
        Please ensure all details below match the candidate&apos;s official identification exactly.
      </Div>

      {/* 5. Candidate information & photo */}
      <Div className="cxcpd-printFormBody">
        <Div className="cxcpd-printFieldsColumn">
          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">First Name:</Span>
              <Span className="cxcpd-printFieldValue">{form.firstName}</Span>
            </Div>
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Last Name:</Span>
              <Span className="cxcpd-printFieldValue">{form.lastName}</Span>
            </Div>
          </Div>

          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Middle Name:</Span>
              <Span className="cxcpd-printFieldValue">{form.middleName || '—'}</Span>
            </Div>
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Previous Reg. No.:</Span>
              <Span className="cxcpd-printFieldValue">{form.previousRegistrationNumber || '—'}</Span>
            </Div>
          </Div>

          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Date of Birth:</Span>
              <Span className="cxcpd-printFieldValue">{formatDobForPrint(form.dateOfBirth)}</Span>
            </Div>
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Gender:</Span>
              <Span className="cxcpd-printCheckboxItem">
                Female <Span className="cxcpd-printBox">{form.gender === 'Female' ? '✓' : ''}</Span>
              </Span>
              <Span className="cxcpd-printCheckboxItem">
                Male <Span className="cxcpd-printBox">{form.gender === 'Male' ? '✓' : ''}</Span>
              </Span>
            </Div>
          </Div>

          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem cxcpd-printFieldItemFull">
              <Span className="cxcpd-printFieldLabel">Residential Address:</Span>
              <Span className="cxcpd-printFieldValue">{form.address}</Span>
            </Div>
          </Div>

          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Cell Phone:</Span>
              <Span className="cxcpd-printFieldValue">{form.cellPhone}</Span>
            </Div>
            <Div className="cxcpd-printFieldItem">
              <Span className="cxcpd-printFieldLabel">Home Phone:</Span>
              <Span className="cxcpd-printFieldValue">{form.homePhone || '—'}</Span>
            </Div>
          </Div>

          <Div className="cxcpd-printFieldsRow">
            <Div className="cxcpd-printFieldItem cxcpd-printFieldItemFull">
              <Span className="cxcpd-printFieldLabel">Email Address:</Span>
              <Span className="cxcpd-printFieldValue">{form.email}</Span>
            </Div>
          </Div>
        </Div>

        <Div className="cxcpd-printPhotoContainer">
          {photoDataUrl ? (
            <Img src={photoDataUrl} alt="Candidate ID Photo" className="cxcpd-printPhotoImg" />
          ) : (
            <Div className="cxcpd-printPhotoPlaceholder">
              <FeatherIcon name="camera" size={26} color="#64748b" />
              <Div>Candidate Photo</Div>
            </Div>
          )}
        </Div>
      </Div>

      {/* 6. Subjects */}
      <Div className="cxcpd-printSectionTitle">Registered Examination Subjects ({selectedSubjects.length})</Div>
      <Table className="cxcpd-printTable">
        <Thead>
          <Tr>
            <Th className="cxcpd-printColSubject">Subject Name</Th>
            <Th className="cxcpd-printColProficiency">Proficiency</Th>
            <Th className="cxcpd-printColEntry">Entry Type</Th>
            <Th className="cxcpd-printColCost">Cost</Th>
          </Tr>
        </Thead>
        <Tbody>
          {selectedSubjects.map(s => (
            <Tr key={s.name}>
              <Td><Strong>{s.name}</Strong></Td>
              <Td>{s.proficiency}</Td>
              <Td>{selection[s.name]?.resit ? <Span className="cxcpd-printResit">Re-sit (SBA Transferred)</Span> : 'Standard Entry'}</Td>
              <Td className="cxcpd-printRightBold">{currency(fees.subject)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* 7. Fees */}
      <Div className="cxcpd-printSectionTitle">Fee Assessment &amp; Invoice Breakdown</Div>
      <Table className="cxcpd-printTable">
        <Thead>
          <Tr>
            <Th className="cxcpd-printColDesc">Description</Th>
            <Th className="cxcpd-printColAmount">Amount (KYD)</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr><Td>Centre Fee (one-off)</Td><Td className="cxcpd-printRight">{currency(fees.centre)}</Td></Tr>
          <Tr><Td>Registration Fee (one-off)</Td><Td className="cxcpd-printRight">{currency(fees.registration)}</Td></Tr>
          <Tr><Td>Processing Fee (one-off)</Td><Td className="cxcpd-printRight">{currency(fees.processing)}</Td></Tr>
          <Tr>
            <Td>Subject Fees ({selectedSubjects.length} subjects × {currency(fees.subject)})</Td>
            <Td className="cxcpd-printRight">{currency(selectedSubjects.length * fees.subject)}</Td>
          </Tr>
          <Tr className="cxcpd-printTotalRow">
            <Td>Total Estimated Payable (at Cayman Academy Office)</Td>
            <Td className="cxcpd-printTotalAmount">{currency(estimatedTotal)} KYD</Td>
          </Tr>
        </Tbody>
      </Table>

      {receipt?.invoiceNumber ? (
        <Div className="cxcpd-printInvoiceRef">Official Invoice Reference: {receipt.invoiceNumber}</Div>
      ) : null}

      {/* 8. Parent commitment & signatures */}
      <Div className="cxcpd-printSectionTitle">Declarations &amp; Digital Signatures</Div>
      <Div className="cxcpd-printCommitment">
        <Strong>Parent / Guardian Commitment:</Strong> I, <U>{form.parentGuardianName || '________________________'}</U>,
        hereby acknowledge that my son/daughter/ward, <B>{candidateFullName || 'the candidate'}</B>, will need to
        diligently prepare for CXC / CSEC examinations. I pledge to give all assistance and guidance to ensure that he/she
        is adequately prepared, and will ensure all financial and administrative commitments are met prior to deadlines.
      </Div>

      <Div className="cxcpd-printSignaturesGrid">
        <Div className="cxcpd-printSigBox">
          <Div className="cxcpd-printSigLabel">Candidate&apos;s Signature</Div>
          {candidateSignatureData ? (
            <Img src={candidateSignatureData} alt="Candidate Signature" className="cxcpd-printSigImage" />
          ) : (
            <Div className="cxcpd-printSigBlank" />
          )}
          <Div className="cxcpd-printSigDate">Date: {todayForPrint()}</Div>
        </Div>

        <Div className="cxcpd-printSigBox">
          <Div className="cxcpd-printSigLabel">Parent / Guardian&apos;s Signature</Div>
          {parentSignatureData ? (
            <Img src={parentSignatureData} alt="Parent Signature" className="cxcpd-printSigImage" />
          ) : (
            <Div className="cxcpd-printSigBlank" />
          )}
          <Div className="cxcpd-printSigDate">Date: {parentSignatureData ? todayForPrint() : '—'}</Div>
        </Div>
      </Div>

      {/* Print-only closing note */}
      <Div className="cxcpd-printFooterNote">
        Thank you for choosing Cayman Academy. All information is confidential and for official use only.
      </Div>
    </Div>
  );
});

export default CxcPrintDocument;

interface CxcPreviewModalProps {
  /** Defaults to the public website's "Review Registration Details". */
  title?: string;
  /** Under the title - e.g. the invoice number and status of a past registration. */
  subtitle?: React.ReactNode;
  error?: string | null;
  submitting?: boolean;
  onClose: () => void;
  onPrint: () => void;
  /** Present on the Save & Preview step; absent when just viewing a past registration (Close only). */
  onConfirm?: () => void;
  /** The rendered CxcPrintDocument (carrying the print ref). */
  children: React.ReactNode;
}

/**
 * The Save & Preview popup - the public website's CxcPreviewModal, same chrome,
 * same actions. Without onConfirm it doubles as the read-only detail popup for
 * a registration already submitted: same document, Print and Close.
 */
export function CxcPreviewModal({ title, subtitle, error = null, submitting = false, onClose, onPrint, onConfirm, children }: CxcPreviewModalProps) {
  return (
    <Div className="cxcpd-modalOverlay">
      <Div className="cxcpd-previewModalContainer">
        <Div className="cxcpd-modalHeader">
          <Div className="cxcpd-modalHeaderGroup">
            <Div>
              <H3 className="cxcpd-modalHeaderTitle">{title ?? 'Review Registration Details'}</H3>
              {subtitle ? <Div className="cxcpd-modalHeaderSubtitle">{subtitle}</Div> : null}
            </Div>
          </Div>
          <Button type="button" className="cxcpd-modalCloseBtn" onClick={onClose} title="Close preview">
            <FeatherIcon name="x" size={20} color="#64748b" />
          </Button>
        </Div>

        <Div className="cxcpd-modalScrollBody">
          {error ? (
            <Div className="cxcpd-alertBox">
              <FeatherIcon name="alert-circle" size={18} color="#991b1b" />
              <Span>{error}</Span>
            </Div>
          ) : null}
          {children}
        </Div>

        <Div className="cxcpd-modalFooter">
          <Button type="button" className="cxcpd-printBtn" onClick={onPrint} title="Print Registration Form">
            <FeatherIcon name="printer" size={16} color="#ffffff" /> Print Registration Form
          </Button>
          <Div className="cxcpd-modalFooterActions">
            {onConfirm ? (
              <>
                <Button type="button" className="cxcpd-cancelBtn" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="button" className="cxcpd-primarybtn" onClick={onConfirm} disabled={submitting}>
                  <FeatherIcon name="send" size={16} color="#ffffff" /> {submitting ? 'Submitting...' : 'Confirm & Submit Application'}
                </Button>
              </>
            ) : (
              <Button type="button" className="cxcpd-cancelBtn" onClick={onClose}>
                Close
              </Button>
            )}
          </Div>
        </Div>
      </Div>
    </Div>
  );
}

/** Status pill for the detail popup's header - the same colours the list's own pill uses. */
export function CxcStatusPill({ label, tone }: { label: string; tone: 'paid' | 'pending' | 'done' }) {
  return <Span className={`cxcpd-statusPill cxcpd-statusPill-${tone}`}>{label}</Span>;
}
